import React, { useState, useEffect } from "react";
import { ChevronDown, Search, Menu, X, Hexagon } from "lucide-react";
import { navigation, siteConfig } from "../data/mockData";

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (href) => {
    if (href && href.startsWith("#")) {
      const el = document.querySelector(href);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      }
    }
    setMobileMenuOpen(false);
    setActiveDropdown(null);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white shadow-md" : "bg-white/95"
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Left Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {navigation.map((item, idx) => (
              <div
                key={idx}
                className="relative"
                onMouseEnter={() => item.hasDropdown && setActiveDropdown(idx)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <button
                  onClick={() => !item.hasDropdown && scrollToSection(item.href)}
                  className="flex items-center gap-1 text-sm font-bold tracking-[0.2em] uppercase text-stone-800 hover:text-orange-600 transition-colors duration-200"
                  style={{ fontFamily: "'Oswald', sans-serif" }}
                >
                  {item.label}
                  {item.hasDropdown && <ChevronDown className="w-3.5 h-3.5" />}
                </button>
                {item.hasDropdown && activeDropdown === idx && (
                  <div className="absolute top-full left-0 mt-1 bg-white shadow-lg rounded-sm py-2 min-w-[200px] z-50">
                    {item.items.map((subItem, subIdx) => (
                      <button
                        key={subIdx}
                        onClick={() => scrollToSection(subItem.href)}
                        className="block w-full text-left px-5 py-2.5 text-sm tracking-wider uppercase text-stone-700 hover:bg-orange-50 hover:text-orange-600 transition-colors"
                        style={{ fontFamily: "'Oswald', sans-serif" }}
                      >
                        {subItem.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* Center Logo */}
          <div className="absolute left-1/2 -translate-x-1/2 -top-1 z-50">
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className="flex items-center justify-center w-24 h-24 bg-white rounded-full shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden p-2"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_mbexim-studio/artifacts/n09jef94_MB%20EXIM.png"
                alt="MB Exim Logo"
                className="w-full h-full object-contain"
              />
            </button>
          </div>

          {/* Right Actions */}
          <div className="hidden lg:flex items-center gap-4">
            <button
              onClick={() => scrollToSection("#contact")}
              className="px-6 py-2.5 border-2 border-stone-800 text-stone-800 text-sm font-bold tracking-[0.15em] uppercase hover:bg-stone-800 hover:text-white transition-all duration-300"
              style={{ fontFamily: "'Oswald', sans-serif" }}
            >
              Contact Us
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden z-50 ml-auto"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6 text-stone-800" />
            ) : (
              <Menu className="w-6 h-6 text-stone-800" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 bg-white z-40 pt-24 px-6">
          <nav className="flex flex-col gap-4">
            {navigation.map((item, idx) => (
              <div key={idx}>
                <button
                  onClick={() => {
                    if (item.hasDropdown) {
                      setActiveDropdown(activeDropdown === idx ? null : idx);
                    } else {
                      scrollToSection(item.href);
                    }
                  }}
                  className="flex items-center gap-2 text-lg font-bold tracking-[0.15em] uppercase text-stone-800"
                  style={{ fontFamily: "'Oswald', sans-serif" }}
                >
                  {item.label}
                  {item.hasDropdown && (
                    <ChevronDown
                      className={`w-4 h-4 transition-transform ${
                        activeDropdown === idx ? "rotate-180" : ""
                      }`}
                    />
                  )}
                </button>
                {item.hasDropdown && activeDropdown === idx && (
                  <div className="pl-4 mt-2 flex flex-col gap-2">
                    {item.items.map((subItem, subIdx) => (
                      <button
                        key={subIdx}
                        onClick={() => scrollToSection(subItem.href)}
                        className="text-left text-base tracking-wider uppercase text-stone-600"
                        style={{ fontFamily: "'Oswald', sans-serif" }}
                      >
                        {subItem.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <button
              onClick={() => scrollToSection("#contact")}
              className="mt-4 px-6 py-3 border-2 border-stone-800 text-stone-800 text-base font-bold tracking-[0.15em] uppercase w-fit"
              style={{ fontFamily: "'Oswald', sans-serif" }}
            >
              Contact Us
            </button>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
